import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Moment from 'moment';
import { StyledScrollContainer } from '../../../components/StyledScrollContainer';
import { DialogPartsDeliveryConfirmation } from '../../../components/DialogPartsDeliveryConfirmation';

import {
    Button,
    Typography,
    Card,
} from "@material-tailwind/react";

import {
    DeliveryResponse,
    listPartsDeliveryRequest,
} from "../../../redux/parts/actions";
import { RootState } from '../../../redux';
import AppImages from '../../../assets/AppImages';

const TABLE_HEAD = [
    {n: "Tracking", className: "w-20"},
    {n: "Parts", className: ""},
    {n: "VIN", className: ""},
    {n: "ETA", className: "w-30 text-center"},
    {n: "", className: "w-20"},
];

export function DashboardPartsDeliveryWeek() {
    const navigation = useNavigate();
    const dispatch = useDispatch();
    const { deliveries } = useSelector((state: RootState) => state.parts);
    const [selectedDelivery, setSelectedDelivery] = useState<DeliveryResponse | null>(null);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        const date_range = getExpectedDateRange('YYYY-MM-DD').split(' - ');

        dispatch(listPartsDeliveryRequest({
            date_from: date_range[0],
            date_to: date_range[1],
        }));
    }, [dispatch]);

    const get_parts = (el: DeliveryResponse) => {
        if (!el?.parts)
            return '';
        return el.parts.reduce((acc, value, index) => (acc + (index == 0 ? '' : ', ') + (value?.name || '')), "");
    };

    const get_vin = (el: DeliveryResponse) => {
        if (!el?.parts)
            return '';
        return el.parts.reduce((acc, value, index) => (acc + (index == 0 ? '' : ', ') + (value?.vehicle?.vin || '')), "");
    };

    const getExpectedDateRange = (format = 'MMM D'): string => {
        const now = Moment();

        const startOfWeek = now.clone().startOf('week').add(1, 'day'); // Adjust for Monday as the first day
        const endOfWeek = now.clone().endOf('week').add(1, 'day'); // Sunday remains the last day

        const formattedStartDate = startOfWeek.format(format); // e.g., "Nov 14"
        const formattedEndDate = endOfWeek.format(format); // e.g., "Nov 20"

        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    return (
        <Card>
            <Card.Header className="w-full m-0 items-center rounded-none flex  justify-between p-4">
                <div className="grow">
                    <Typography
                        type="lead"
                        color="default"
                        className="font-semibold mb-1"
                    >
                        Parts Deliveries
                    </Typography>
                    <Typography type="small" className="text-foreground block">
                        All parts deliveries expected on {getExpectedDateRange()}
                    </Typography>
                </div>
                <div className="flex flex-nowrap">
                    <Button
                        variant="outline"
                        color="secondary"
                        onClick={() => navigation('/dashboard/parts/deliveries')}
                        className="flex items-center shrink-0 gap-2 sm:w-auto w-full"
                    >
                        See All
                    </Button>
                </div>
            </Card.Header>
            <StyledScrollContainer className="max-h-[400px]">
                <Card.Body className="p-0 rounded-none">
                    <table className="w-full min-w-max table-auto text-left">
                        <thead>
                            <tr>
                                {
                                    TABLE_HEAD.map((head, idx:number) => (
                                        <th key={idx} className={`border-b border-surface p-4 ${head.className}`}>
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="block font-medium"
                                            >
                                                {head.n}
                                            </Typography>
                                        </th>
                                    ))
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {
                                deliveries.map((el: DeliveryResponse, idx: number) => (
                                    <tr key={idx} className="group">
                                        <td className="p-4 border-b border-surface group-last:border-none">
                                            <div className="flex items-center gap-3">
                                                {/*
                                                <Avatar src={el?.thumbnail?.full_url} alt={el.ymmt} shape="rounded" />
                                                  */}
                                                <div className="space-y-1">
                                                    <Typography
                                                        type="small"
                                                        color="default"
                                                        className="font-semibold w-20 whitespace-nowrap overflow-hidden text-ellipsis block"
                                                    >
                                                        {el.delivery_order}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 border-b border-surface group-last:border-none">
                                            <Typography type="small" className="whitespace-nowrap overflow-hidden text-ellipsis block text-foreground">
                                                {get_parts(el)}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-surface group-last:border-none">
                                            <Typography type="small" className="whitespace-nowrap overflow-hidden text-ellipsis block text-foreground">
                                                {get_vin(el)}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-surface group-last:border-none text-center">
                                            <Typography type="small" className="whitespace-nowrap overflow-hidden w-30 text-center text-ellipsis block text-foreground">
                                                {el.delivery_est_date || 'N/A'}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-surface w-20 group-last:border-none">
                                            <Button
                                                size="xs"
                                                variant="outline"
                                                color="secondary"
                                                className="border-[#E2E8F0]"
                                                onClick={() => {
                                                    setSelectedDelivery(el);
                                                    setOpenDialog(true);
                                                }}
                                            >
                                                Delivered
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>

                    {deliveries.length == 0 &&
                        <div className="flex flex-col my-20 gap-5 justify-center items-center h-full">
                            <div className="flex bg-secondary items-center p-4 rounded-full">
                                <AppImages.IconParts className="w-6 h-6" />
                            </div>
                            <Typography className="text-primary font-semibold text-2xl">
                                No expected parts deliveries
                            </Typography>
                        </div>
                    }
                </Card.Body>
            </StyledScrollContainer>

            <DialogPartsDeliveryConfirmation
                delivery={selectedDelivery}
                setIsOpen={setOpenDialog}
                isOpen={openDialog}
            />
        </Card>
    );
}
